import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Smartphone, Wrench, Shield, MapPin, Gift, RefreshCw, BookOpen, Home, ChevronDown, Search } from 'lucide-react';
import { useContent } from '../context/ContentContext';
import { isBoltPath, boltHref, BOLT_PREFIX } from '../utils/boltMod';
import useModalA11y from '../hooks/useModalA11y';
import { trackCallClick, trackFilterClick } from '../utils/analytics';
import { fetchJsonCached } from '../utils/apiCache';
import { getCategory, PHONE_CATEGORIES, categorySlug } from '../constants/categories';
import { PRIMARY_PHONE, PRIMARY_PHONE_DISPLAY, STORES } from '../constants/stores';
import { csupaszUtvonal } from '../constants/ctaMentesOldalak';
import { withSlash } from '../utils/url';

// A fejléc hívógombjának elérhető neve – a LÁTHATÓ számot is tartalmazza
// (WCAG 2.5.3 Label in Name), és egy helyről jön, mint maga a szám.
const BELVAROS_CALL_LABEL = `${STORES.belvaros.shortLabel} hívása: ${STORES.belvaros.phoneDisplay}`;

const STOREFRONT_API = `${process.env.REACT_APP_BACKEND_URL}/api/storefront/devices`;

// A beépített logó ismert asszet: 256×256, és van hozzá veszteségmentes .webp.
// A CMS-ből felülírt logónál egyik sem garantált, ezért ott marad a sima <img>.
const DEFAULT_LOGO = "/logo-face.png";

const Header = () => {
  const { content } = useContent();
  const companyInfo = content?.companyInfo || {
    name: "NorbiPhone",
    logo: DEFAULT_LOGO
  };
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // K8: a mobilmenü eddig NEM volt modál – nem volt `role="dialog"`, az ESC nem
  // zárta, a Tab a nyitott menü MÖGÉ ugrott (a háttér „Hívás most” gombjára),
  // és a lap mögötte görgethető maradt. Külső auditon mérve. Ugyanaz a hook,
  // ami a termék-, foglalás-, szűrő- és 360°-modálban már működik.
  // Ez az EGYETLEN navigációs belépő mobilon – itt a legdrágább a hiba.
  const mobileMenuRef = useRef(null);
  // ── Elfér-e a vízszintes menü? ────────────────────────────────────────────
  // Fix töréspont (xl = 1280 px) nem elég: a szövegszélesség betűtípustól
  // (Mac/Windows), nagyítástól és a CMS-logó méretétől függ – Macen 1280 px
  // fölött is két sorba tört a menü, és a hívás-gomb kilógott. Ezért MÉRÜNK:
  // ha a logó + a menü természetes szélessége + a jobb oldali gombok nem férnek
  // ki, xl fölött is a hamburger-menü jön. Prerenderben/első festéskor a teljes
  // menü látszik (navFits = true), a mérés utána igazít.
  const rowRef = useRef(null);
  const logoRef = useRef(null);
  const navRef = useRef(null);
  const ctaRef = useRef(null);
  const [navFits, setNavFits] = useState(true);
  const [isCatAccordionOpen, setIsCatAccordionOpen] = useState(false);
  const [isServiceAccordionOpen, setIsServiceAccordionOpen] = useState(false);

  // Csupasz oldalakon (pl. /nyitva) nincs fejléc – ott a teljes navigáció
  // elvinné a figyelmet az egyetlen fontos akcióról. Ld. ctaMentesOldalak.js.
  // MINDEN hook fölötte fut, hogy a hook-sorrend renderenként azonos legyen.
  const [catCounts, setCatCounts] = useState({});
  const location = useLocation();
  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);
  useModalA11y(mobileMenuRef, { active: isMobileMenuOpen, onEscape: closeMobileMenu });

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Kategória-darabszámok UGYANABBÓL a forrásból, mint a /telefonok oldal
  // (storefront proxy, közös cache) – NINCS hardcode-olt szám. Halkan elbukik.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const json = await fetchJsonCached(STOREFRONT_API, { ttl: 120000 });
        if (!alive) return;
        const counts = {};
        for (const d of (json.data || [])) {
          const cat = getCategory(d.model);
          counts[cat] = (counts[cat] || 0) + 1;
        }
        setCatCounts(counts);
      } catch (err) { /* silent fail – a menü számok nélkül is működik */ }
    })();
    return () => { alive = false; };
  }, []);

  // Csak a valóban készleten lévő kategóriák, a közös sorrendben.
  const catItems = useMemo(
    () => PHONE_CATEGORIES.filter((c) => (catCounts[c.key] || 0) > 0),
    [catCounts]
  );

  // A „Szerviz” pont legördülője. Ezek MEGLÉVŐ, önálló szolgáltatás-oldalak –
  // eddig csak keresőből voltak elérhetők, a menüből nem. A fejléc így nem nő
  // tovább (a 8 pont már kitölti a sávot 1152 px-en), és a szerviz-aloldalak
  // mégis kapnak belső linket.
  const serviceItems = [
    { href: '/iphone-kijelzocsere-oroshaza/',     label: 'iPhone kijelzőcsere' },
    { href: '/iphone-akkumulatorcsere-oroshaza/', label: 'iPhone akkumulátorcsere' },
    { href: '/iphone-hatlapcsere/',               label: 'iPhone hátlapcsere' },
    { href: '/samsung-kijelzocsere-oroshaza/',    label: 'Samsung kijelzőcsere' },
    { href: '/samsung-xiaomi-akkumulatorcsere/',  label: 'Samsung / Xiaomi akkucsere' },
  ];

  // priority: 'primary' = legerősebb nav pont, 'highlight' = VIP sárga, undefined = alap
  // Bolt mód (/bolt/...): szerviz-mentes menü hirdetési platformokhoz – csak a
  // három bolt-oldal (utils/boltMod.js). Az eredeti menü változatlan.
  const bolt = isBoltPath(location.pathname);
  const navLinks = bolt ? [
    { href: '/bolt/telefonok/',   label: 'Használt telefonok',  icon: Smartphone, priority: 'primary' },
    { href: '/bolt/felvasarlas/', label: 'Készülékfelvásárlás', icon: RefreshCw },
    { href: '/bolt/uzletek/',     label: 'Üzleteink',           icon: MapPin },
  ] : [
    { href: '/',           label: 'Kezdőlap',          icon: Home },
    { href: '/telefonok/',  label: 'Használt telefonok', icon: Smartphone, priority: 'primary' },
    { href: '/szerviz/',    label: 'Szerviz',             icon: Wrench,     priority: 'primary' },
    { href: '/hidrogel/',   label: 'Hidrogél fólia',      icon: Shield },
    { href: '/felvasarlas/',label: 'Készülékfelvásárlás',         icon: RefreshCw },
    { href: '/uzletek/',    label: 'Üzleteink',           icon: MapPin },
    { href: '/blog/',       label: 'Blog',                icon: BookOpen },
    { href: '/telefonvadasz/', label: 'Telefonvadász',    icon: Search },
    { href: '/vip-klub/',   label: 'VIP Klub',            icon: Gift,       priority: 'highlight' },
  ];

  // A lenyílók a menüpontot perjel NÉLKÜLI alakkal azonosítják. Az audit #6 óta
  // a href-ek perjelesek ('/szerviz/'), a feltétel viszont a régi '/szerviz'-t
  // várta – emiatt tűnt el a Szerviz és a Használt telefonok lenyíló is.
  const menuKey = (href) => href.replace(/\/$/, '').replace(new RegExp(`^${BOLT_PREFIX}(?=/)`), '') || '/';
  // a lenyílók linkjei bolt módban a /bolt/ oldalakra mutatnak
  const lnk = (href) => (bolt ? boltHref(href) : href);
  // A vízszintes menüből a „Kezdőlap” kimarad: a logó oda visz (alt: „– kezdőlap”),
  // és a 9 pont így fér el. Mobilon a listában marad.
  const desktopLinks = navLinks.filter((l) => l.href !== '/');

  // A menü href-jei záró perjelesek (audit #6), a pathname viszont lehet
  // perjel nélküli is (SPA-navigáció, régi link) – mindkettőt perjel NÉLKÜL
  // vetjük össze, így a /szerviz és a /szerviz/ is aktívnak számít, a
  // /szervizfoo viszont nem.
  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    const cel = href.split(/[?#]/)[0].replace(/\/$/, '');
    const ut = location.pathname.replace(/\/$/, '');
    return ut === cel || ut.startsWith(`${cel}/`);
  };

  // Desktop link stílusok – ikonok nélkül, szövegalapú, kompakt padding
  const desktopLinkClass = (link) => {
    const active = isActive(link.href);
    if (link.priority === 'highlight') {
      return 'whitespace-nowrap px-2.5 py-2 rounded-full font-semibold text-sm transition-all duration-200 bg-gradient-to-r from-yellow-400/20 to-yellow-500/20 text-yellow-700 border border-yellow-400/30 hover:from-yellow-400/30 hover:to-yellow-500/30';
    }
    if (link.priority === 'primary') {
      return `whitespace-nowrap px-2.5 py-2 rounded-full font-semibold text-sm transition-all duration-200 ${
        active
          ? 'bg-glow-from/25 text-[rgb(0,55,32)]'
          : 'text-[rgb(0,55,32)] hover:bg-glow-from/15'
      }`;
    }
    // alap menüpont
    return `whitespace-nowrap px-2.5 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
      active
        ? 'bg-glow-from/20 text-[rgb(0,55,32)]'
        : 'text-gray-500 hover:text-[rgb(0,55,32)] hover:bg-black/5'
    }`;
  };

  // Mobil link stílusok – ikonnal, teljes szélesség
  const mobileLinkClass = (link) => {
    const active = isActive(link.href);
    if (link.priority === 'highlight') {
      return 'flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 bg-gradient-to-r from-yellow-400/10 to-yellow-500/10 text-yellow-700 border border-yellow-400/20';
    }
    if (link.priority === 'primary') {
      return `flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 ${
        active
          ? 'bg-glow-from/20 text-[rgb(0,55,32)]'
          : 'text-[rgb(0,55,32)] hover:bg-glow-from/10'
      }`;
    }
    return `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
      active
        ? 'bg-glow-from/20 text-[rgb(0,55,32)]'
        : 'text-gray-600 hover:text-[rgb(0,55,32)] hover:bg-black/5'
    }`;
  };

  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof window === 'undefined') return undefined;
    const meres = () => {
      const nav = navRef.current;
      if (!nav || !logoRef.current || !ctaRef.current) return;
      if (window.innerWidth < 1280) { setNavFits(true); return; }  // xl alatt amúgy is hamburger
      const kell = logoRef.current.offsetWidth + nav.scrollWidth + ctaRef.current.offsetWidth + 48;
      setNavFits(kell <= row.clientWidth);
    };
    meres();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(meres) : null;
    ro?.observe(row);
    if (navRef.current) ro?.observe(navRef.current);
    window.addEventListener('resize', meres);
    document.fonts?.ready?.then(meres).catch(() => {});
    return () => { ro?.disconnect(); window.removeEventListener('resize', meres); };
  }, [catItems.length]);

  // Csupasz oldalon nincs fejléc – MINDEN hook fölötte futott le.
  if (csupaszUtvonal(location.pathname)) return null;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-full bg-white/95 backdrop-blur-xl border border-black/10 shadow-lg'
          : 'bg-white border-b border-gray-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div ref={rowRef} className="relative flex items-center justify-between gap-4 h-14 sm:h-16">

          {/* Logo → Kezdőlap */}
          <Link ref={logoRef} to={bolt ? `${BOLT_PREFIX}/telefonok/` : "/"} className="flex items-center gap-3 flex-shrink-0" data-testid="header-logo-link">
            {/* width/height: `w-auto` mellett a böngésző a betöltésig nem tudja a
                logó szélességét → a fejléc tartalma elugrik (CLS). NEM lazy: a
                fejléc minden oldalon a hajtás felett van. */}
            {companyInfo.logo === DEFAULT_LOGO ? (
              <picture>
                <source srcSet="/logo-face.webp" type="image/webp" />
                <img
                  src={DEFAULT_LOGO}
                  alt={`${companyInfo.name} – kezdőlap`}
                  className="h-10 sm:h-12 w-auto"
                  width="256"
                  height="256"
                  decoding="async"
                  data-testid="header-logo"
                />
              </picture>
            ) : (
              <img
                src={companyInfo.logo}
                alt={`${companyInfo.name} – kezdőlap`}
                className="h-10 sm:h-12 w-auto"
                decoding="async"
                data-testid="header-logo"
              />
            )}
          </Link>

          {/* Desktop Navigation – xl+ (1280px), szövegalapú, kompakt */}
          {/* A vízszintes menü 1280 px-től jelenik meg (Tailwind `xl`), nem
              1152-től. Ok: a nyolc menüpont természetes szélessége ~939 px, de
              1152 px-en csak ~844 px jut nekik, ezért három felirat KÉT SORBA
              tört („Használt telefonok”, „Hidrogél fólia”, „VIP Klub”).
              1280 px felett mind egy sorban elfér; alatta a hamburger menü jön,
              ahol bőven van hely. */}
          <nav
            ref={navRef}
            aria-label="Főnavigáció"
            aria-hidden={navFits ? undefined : 'true'}
            className={`hidden xl:flex items-center gap-0.5 ${navFits ? '' : 'absolute left-0 top-0 invisible pointer-events-none'}`}
          >
            {desktopLinks.map((link) => {
              // A „Használt telefonok” pont legördülőt kap a kategóriákkal +
              // darabszámokkal (hover / fókusz nyitja, tiszta CSS – nincs extra JS).
              if (menuKey(link.href) === '/telefonok' && catItems.length > 0) {
                return (
                  <div key={link.href} className="relative group" data-testid="header-cat-dropdown">
                    <Link
                      to={link.href}
                      data-testid={`header-nav-link-${link.href.replace('/', '') || 'home'}`}
                      className={`${desktopLinkClass(link)} inline-flex items-center gap-1`}
                    >
                      {link.label}
                      <ChevronDown size={14} className="opacity-70 transition-transform group-hover:rotate-180" />
                    </Link>
                    <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity duration-150 absolute left-0 top-full pt-2 w-60 z-50">
                      <div className="bg-white rounded-2xl border border-black/10 shadow-xl p-2 max-h-[70vh] overflow-y-auto">
                        {catItems.map((c) => (
                          <Link
                            key={c.key}
                            to={lnk(withSlash(`/telefonok?kategoria=${categorySlug(c.key)}`))}
                            onClick={() => trackFilterClick(c.key)}
                            data-testid={`header-cat-${categorySlug(c.key)}`}
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl text-sm font-medium text-gray-700 hover:bg-glow-from/10 hover:text-[rgb(0,55,32)] transition-colors"
                          >
                            <span className="flex items-center gap-2"><span aria-hidden="true">{c.icon}</span>{c.label}</span>
                            <span className="text-xs text-gray-500 tabular-nums">{catCounts[c.key]}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              // A „Szerviz” pont ugyanezzel a mintával kapja meg a
              // szolgáltatás-aloldalakat. A szülő link továbbra is a /szerviz-re visz.
              if (menuKey(link.href) === '/szerviz') {
                return (
                  <div key={link.href} className="relative group" data-testid="header-service-dropdown">
                    <Link
                      to={link.href}
                      data-testid={`header-nav-link-${link.href.replace('/', '') || 'home'}`}
                      className={`${desktopLinkClass(link)} inline-flex items-center gap-1`}
                    >
                      {link.label}
                      <ChevronDown size={14} className="opacity-70 transition-transform group-hover:rotate-180" />
                    </Link>
                    <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity duration-150 absolute left-0 top-full pt-2 w-64 z-50">
                      <div className="bg-white rounded-2xl border border-black/10 shadow-xl p-2">
                        {serviceItems.map((sv) => (
                          <Link
                            key={sv.href}
                            to={sv.href}
                            data-testid={`header-service-${sv.href.replace(/\//g, '')}`}
                            className="block px-3 py-2 rounded-xl text-sm font-medium text-gray-700 hover:bg-glow-from/10 hover:text-[rgb(0,55,32)] transition-colors"
                          >
                            {sv.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`header-nav-link-${link.href.replace('/', '') || 'home'}`}
                  className={desktopLinkClass(link)}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* CTA + hamburger sor */}
          <div ref={ctaRef} className="flex items-center gap-2 flex-shrink-0">
            {/* Hívj minket gomb – sm+ */}
            {/* A telefonszám maga BIZALMI JEL: aki látja a számot, elhiszi, hogy
                van kit hívni. Ezért desktopon (lg+, 1024px-től) a gomb SZÖVEGE
                maga a szám, nem egy általános felszólítás. Kisebb kijelzőn
                marad a rövid „Hívj minket!” (a szám kifutna a fejlécből).
                A szám mindig a Belvárosé – ez a fő kontakt-szám. */}
            <a
              href={`tel:${PRIMARY_PHONE}`}
              onClick={() => trackCallClick('header')}
              data-testid="header-cta-call"
              aria-label={BELVAROS_CALL_LABEL}
              className="hidden sm:flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-gradient-to-br from-glow-from to-glow-to text-white font-semibold text-sm hover:scale-105 transition-transform duration-200 shadow-md hover:shadow-lg flex-shrink-0"
            >
              <Phone size={16} aria-hidden="true" />
              <span className="hidden sm:inline lg:hidden">Hívj minket!</span>
              <span className="hidden lg:inline tabular-nums whitespace-nowrap" data-testid="header-cta-phone">
                {PRIMARY_PHONE_DISPLAY}
              </span>
            </a>

            {/* Hamburger – xl-ig látható */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="header-mobile-menu-toggle"
              aria-label={isMobileMenuOpen ? 'Menü bezárása' : 'Menü megnyitása'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobil-fomenu"
              className={`${navFits ? 'xl:hidden' : ''} relative p-2 rounded-full hover:bg-black/5 transition-colors after:absolute after:-inset-1 after:content-['']`}
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobil menü panel – xl-ig */}
      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          id="mobil-fomenu"
          role="dialog"
          aria-modal="true"
          aria-label="Főmenü"
          tabIndex={-1}
          /* `bg-white` (tömör) a korábbi `bg-white/95` helyett: a 95%-os
             áttetszőség + blur ellenére olvashatóan átütött a háttér-tartalom
             a menüpontok alatt (auditon mérve). */
          className={`${navFits ? 'xl:hidden' : ''} absolute top-full left-2 right-2 mt-2 p-3 bg-white rounded-2xl border border-black/10 shadow-xl outline-none`}>
          <nav aria-label="Mobil főmenü" className="flex flex-col gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              // Mobil: „Használt telefonok” accordionként a kategóriákkal.
              if (menuKey(link.href) === '/telefonok' && catItems.length > 0) {
                return (
                  <div key={link.href} className="flex flex-col">
                    <div className={`${mobileLinkClass(link)} !justify-between`}>
                      <Link
                        to={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        data-testid={`header-mobile-link-${link.href.replace('/', '') || 'home'}`}
                        className="flex items-center gap-3 flex-1"
                      >
                        <Icon size={18} />
                        {link.label}
                      </Link>
                      <button
                        type="button"
                        onClick={() => setIsCatAccordionOpen((v) => !v)}
                        aria-expanded={isCatAccordionOpen}
                        aria-label={isCatAccordionOpen ? 'Kategóriák bezárása' : 'Kategóriák megnyitása'}
                        data-testid="header-mobile-cat-toggle"
                        className="p-1.5 -mr-1 rounded-lg hover:bg-black/5"
                      >
                        <ChevronDown size={18} className={`transition-transform ${isCatAccordionOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    {isCatAccordionOpen && (
                      <div className="flex flex-col gap-0.5 pl-9 pr-1 pb-1" data-testid="header-mobile-cat-list">
                        {catItems.map((c) => (
                          <Link
                            key={c.key}
                            to={lnk(withSlash(`/telefonok?kategoria=${categorySlug(c.key)}`))}
                            onClick={() => { trackFilterClick(c.key); setIsMobileMenuOpen(false); }}
                            data-testid={`header-mobile-cat-${categorySlug(c.key)}`}
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-glow-from/10 hover:text-[rgb(0,55,32)]"
                          >
                            <span className="flex items-center gap-2"><span aria-hidden="true">{c.icon}</span>{c.label}</span>
                            <span className="text-xs text-gray-500 tabular-nums">{catCounts[c.key]}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              if (menuKey(link.href) === '/szerviz') {
                return (
                  <div key={link.href} className="flex flex-col">
                    <div className={`${mobileLinkClass(link)} !justify-between`}>
                      <Link
                        to={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        data-testid={`header-mobile-link-${link.href.replace('/', '') || 'home'}`}
                        className="flex items-center gap-3 flex-1"
                      >
                        <Icon size={18} />
                        {link.label}
                      </Link>
                      <button
                        type="button"
                        onClick={() => setIsServiceAccordionOpen((v) => !v)}
                        aria-expanded={isServiceAccordionOpen}
                        aria-label={isServiceAccordionOpen ? 'Szolgáltatások bezárása' : 'Szolgáltatások megnyitása'}
                        data-testid="header-mobile-service-toggle"
                        className="p-1.5 -mr-1 rounded-lg hover:bg-black/5"
                      >
                        <ChevronDown size={18} className={`transition-transform ${isServiceAccordionOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    {isServiceAccordionOpen && (
                      <div className="flex flex-col gap-0.5 pl-9 pr-1 pb-1" data-testid="header-mobile-service-list">
                        {serviceItems.map((sv) => (
                          <Link
                            key={sv.href}
                            to={sv.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            data-testid={`header-mobile-service-${sv.href.replace(/\//g, '')}`}
                            className="px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-glow-from/10 hover:text-[rgb(0,55,32)]"
                          >
                            {sv.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`header-mobile-link-${link.href.replace('/', '') || 'home'}`}
                  className={mobileLinkClass(link)}
                >
                  <Icon size={18} />
                  {link.label}
                </Link>
              );
            })}

            {/* Hívj minket – mobil CTA a menü alján */}
            <a
              href={`tel:${PRIMARY_PHONE}`}
              className="flex items-center justify-center gap-2 px-5 py-3 mt-2 rounded-full bg-gradient-to-br from-glow-from to-glow-to text-white font-semibold"
              onClick={() => { trackCallClick('header_mobile'); setIsMobileMenuOpen(false); }}
            >
              <Phone size={18} />
              Hívj minket!
            </a>
          </nav>
          {/* Billentyűzettel is elérhető bezárás a fókuszcsapdán BELÜL – az X
              toggle a dialóguson KÍVÜL ül, Tab-bal nem volt elérhető. Csak
              fókuszra válik láthatóvá, így a vizuális menü változatlan; a DOM
              végén van, tehát a kezdő fókusz továbbra is az első menüpont. */}
          <button
            type="button"
            onClick={closeMobileMenu}
            className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:right-2 focus:p-2 focus:rounded-full focus:bg-white focus:shadow"
            aria-label="Menü bezárása"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;
